<script lang="ts">
	import Container from '@components/global/Container.svelte';
	import SearchInput from '@components/global/SearchInput.svelte';
	import AuthCtaButtons from '@components/global/micro/AuthCTAButtons.svelte';
	import { afterNavigate, goto, pushState } from '$app/navigation';
	import { page } from '$app/stores';
	import NavLogo from '@components/global/micro/NavLogo.svelte';
	import { PUBLIC_SITE_NAME } from '$env/static/public';
	import RoundPhoneAndroid from 'virtual:icons/ic/round-phone-android';
	import BagHandleOutline from 'virtual:icons/ion/bag-handle-outline';
	import { headerHeight, isExpandedSearch } from '@lib/stores/header.store';
	import LinkWithPanel from '@components/global/Header/LinkWithPanel.svelte';
	import { fade } from 'svelte/transition';
	import { links } from './data';
	import DownloadPanel from './DownloadPanel.svelte';
	import MenuOutline from 'virtual:icons/ion/menu-outline';
	import MenuPanel from '@components/global/Header/MenuPanel.svelte';
	import MediaQuery from 'svelte-media-queries';
	import CartPanel from './CartPanel.svelte';
	import Avatar from './Avatar.svelte';

	export let isOnlyLogo = false,
		isIncludeBackUrl = false,
		isIncludeUtilityNav = true;

	let scrollY: number;

	const showState: Record<string, boolean> = {
		cart: false,
		download: false,
		profile: false,
		search: false
	};

	const { isLoggedIn } = $page.data;

	function gotoCartUrl() {
		const cartUrl = new URL(`/cart`, $page.url.origin);
		const currentUrl = $page.url;
		const backUrl = $page.url.searchParams.get('back_url');

		cartUrl.searchParams.set('back_url', backUrl ?? currentUrl.href);
		goto(cartUrl);
	}

	afterNavigate(() => {
		// Set to false all show states
		Object.keys(showState).forEach((key) => (showState[key] = false));
	});

	$: ({ cartData } = $page.data);

	const openMenuPanel = () => pushState('', { showMenuPanel: true });
</script>

<svelte:window bind:scrollY />

<header
	bind:offsetHeight={$headerHeight}
	class="sticky top-0 z-[999] bg-base-100"
	class:shadow={scrollY > 10 || isIncludeBackUrl}
>
	<slot name="header-top-row" />
	{#if isIncludeUtilityNav}
		<div class="utility-nav border-b bg-base-200 py-2">
			<Container>
				<div class="flex items-center justify-between text-xs text-gray-500">
					<LinkWithPanel bind:isShowPanel={showState.download} panelPlacement="bottom-start">
						<a
							slot="trigger"
							href="/download-app"
							class="link-hover link flex items-center gap-1 font-medium"
							class:underline={$page.url.pathname === '/download-app'}
						>
							<RoundPhoneAndroid class="text-base" />
							Download Aplikasi {PUBLIC_SITE_NAME}
						</a>
						<div slot="content" class="max-w-sm rounded-xl border bg-base-100 p-6 shadow-soft">
							<DownloadPanel />
						</div>
					</LinkWithPanel>
					<ul class="flex items-center gap-6">
						{#each links.quick as link}
							{@const isActive = $page.url.pathname.includes(link.url)}
							<li>
								<a class="link-hover link" class:underline={isActive} href={link.url}
									>{link.label}</a
								>
							</li>
						{/each}
					</ul>
				</div>
			</Container>
		</div>
	{/if}
	<Container>
		<div class="flex items-center justify-between gap-4 py-4">
			{#if !$isExpandedSearch}
				<slot name="header-logo">
					<NavLogo isPriority={true} />
				</slot>
			{/if}
			{#if !isOnlyLogo}
				<div
					class="flex flex-1 items-center justify-end gap-4 pre-md:w-full pre-md:gap-5"
					class:w-full={$isExpandedSearch}
				>
					<SearchInput bind:isFocused={showState.search} />
					{#if !$isExpandedSearch}
						<LinkWithPanel bind:isShowPanel={showState.cart} panelPlacement="bottom">
							<button
								slot="trigger"
								on:click={gotoCartUrl}
								type="button"
								class="relative"
								aria-label="Keranjang"
							>
								<BagHandleOutline class="text-3xl" />
								{#if cartData && cartData?.totalItems > 0}
									<span
										class="absolute bottom-0 right-0 grid size-5 place-items-center rounded-full bg-error text-[0.6rem] leading-none text-base-100"
										>{cartData?.totalItems}</span
									>
								{/if}
							</button>
							<div slot="content" class="relative z-[1]">
								<CartPanel />
							</div>
						</LinkWithPanel>
						{#if isLoggedIn}
							<div class="avatar-pic">
								<Avatar bind:isShowPanel={showState.profile} />
							</div>
						{/if}
						<button
							on:click={openMenuPanel}
							type="button"
							aria-label="Menu"
							title="Menu"
							class="pre-lg:hidden"
						>
							<MenuOutline class="text-3xl" />
						</button>
						<MediaQuery query="(max-width: 911px)" let:matches>
							{#if matches && $page.state.showMenuPanel}
								<MenuPanel on:close={() => history.back()} />
							{/if}
						</MediaQuery>
					{/if}
					{#if !isLoggedIn}
						<AuthCtaButtons class="hidden border-l border-gray-300 pl-4 pre-lg:flex pre-lg:pl-5" />
					{/if}
				</div>
			{:else}
				<slot name="right-side" />
			{/if}
		</div>
	</Container>
</header>

{#if Object.values(showState).some((state) => state)}
	<div
		transition:fade={{ duration: 350 }}
		class="overlay fixed left-0 top-0 z-[100] size-full bg-black/30"
	></div>
{/if}

<style lang="postcss">
	@media (pointer: coarse) {
		.utility-nav {
			@apply hidden;
		}

		.avatar-pic {
			@apply hidden pre-lg:block;
		}
	}
</style>
